import { Stack, Typography } from "@mui/material";
import React from "react";
import { useTheme } from "@mui/material";
import { Editor } from "@tiptap/core";
import { Input } from "components/input";
import { Button } from "components/button";
import { isUrlValid } from "utilz/helpers/isUrlValid";

export const LinkPopover = ({
  editor,
  onClose,
}: {
  editor: Editor;
  onClose?: () => void;
}) => {
  const theme = useTheme();
  const selectedText = editor.state.doc.textBetween(
    editor.state.selection.from,
    editor.state.selection.to,
  );

  // Get existing link attributes if the selection has a link
  const existingLink = editor.getAttributes("link");
  const existingHref = existingLink?.href || "";

  const [linkName, setLinkName] = React.useState(selectedText);
  const [linkUrl, setLinkUrl] = React.useState(existingHref);
  const [urlError, setUrlError] = React.useState("");

  const normalizeUrl = (url: string): string => {
    const trimmedUrl = url.trim();
    if (!trimmedUrl) return "";

    // Check if URL already has a protocol
    if (/^https?:\/\//i.test(trimmedUrl)) {
      return trimmedUrl;
    }

    // Append https:// if no protocol
    return `https://${trimmedUrl}`;
  };

  const validateUrl = (url: string): boolean => {
    if (!url.trim()) {
      setUrlError("URL is required");
      return false;
    }

    if (!isUrlValid(url)) {
      setUrlError("Please enter a valid URL (e.g., example.com)");
      return false;
    }

    setUrlError("");
    return true;
  };

  const setLink = () => {
    if (!validateUrl(linkUrl)) {
      return;
    }

    const normalizedUrl = normalizeUrl(linkUrl);
    const { from } = editor.state.selection;
    editor
      .chain()
      .focus()
      .deleteSelection()
      .insertContent(linkName)
      .setTextSelection({ from, to: from + linkName.length })
      .setLink({ href: normalizedUrl, target: "_blank" })
      .run();
    onClose?.();
  };

  return (
    <Stack
      sx={{
        width: "410px",
        // height: "240px",
        border: `1px solid ${theme.palette.neutral["020"]}`,
        borderRadius: "8px",
        overflow: "hidden",
        backgroundColor: theme.palette.primitive.system.violet[50],
        boxShadow: "-4px 9px 11px 0px #00000014",
        p: "16px",
        gap: "16px",
      }}
    >
      <Typography variant="subtitle1">Edit Link</Typography>
      <Input
        placeholder="Enter link name"
        name="linkName"
        defaultValue={selectedText}
        onChange={(e) => setLinkName(e.target.value)}
      />
      <Input
        placeholder="Enter link url"
        name="linkUrl"
        defaultValue={existingHref}
        onChange={(e) => {
          setLinkUrl(e.target.value);
          if (urlError) setUrlError("");
        }}
        error={!!urlError}
        errorMessage={urlError}
      />
      <Stack direction="row" justifyContent="flex-end" gap="8px">
        <Button
          variant="secondary"
          fullWidth
          onClick={() => {
            onClose?.();
          }}
        >
          Cancel
        </Button>
        <Button variant="primary" fullWidth onClick={setLink}>
          Save
        </Button>
      </Stack>
    </Stack>
  );
};
